import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Edit, Trash2, CheckCircle2, RefreshCw, ChevronDown, ChevronRight,
  FileText, Link as LinkIcon, Mail, Download, MessageSquare, Inbox
} from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS, API, BACKEND_URL } from '@/constants/bluemonk';

export const AdminInputRequestsSection = ({ items, headers, onRefresh, onEdit, onAdd }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this input request?')) return;
    
    try {
      await axios.delete(`${API}/input-requests/${id}`, { headers });
      toast.success('Input request deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleReview = async (id, action, reason = '') => {
    try {
      await axios.post(`${API}/input-requests/${id}/review`, { action, reason }, { headers });
      toast.success(action === 'accept' ? 'Submission accepted' : 'Revision requested');
      onRefresh();
    } catch (error) {
      toast.error('Failed to review');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  const getSubmissionIcon = (type) => {
    switch (type) {
      case 'file': return <FileText className="h-4 w-4 text-blue-500" />;
      case 'link': return <LinkIcon className="h-4 w-4 text-purple-500" />;
      case 'shared-via-email': return <Mail className="h-4 w-4 text-amber-500" />;
      default: return <MessageSquare className="h-4 w-4 text-green-500" />;
    }
  };

  // Sort: items with submissions first, then by status
  const sortedItems = [...items].sort((a, b) => {
    // Submitted items first
    if (a.status === 'submitted' && b.status !== 'submitted') return -1;
    if (b.status === 'submitted' && a.status !== 'submitted') return 1;
    // Then by submission count
    const aCount = a.submissions?.length || 0;
    const bCount = b.submissions?.length || 0;
    if (aCount !== bCount) return bCount - aCount;
    // Then by deadline
    return new Date(a.deadline) - new Date(b.deadline);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Input Requests</h1>
        <Button onClick={onAdd} className="bg-primary hover:bg-primary/90" data-testid="add-input-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Input Request
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="p-8 text-center">
          <Inbox className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-700">No input requests</p>
          <p className="text-slate-500">Create your first input request to get started</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedItems.map((item) => {
            const colors = BRAND_COLORS[item.brand];
            const isOverdue = item.status === 'pending' && item.deadline < today;
            const isExpanded = expandedItems.has(item.id);
            const hasSubmissions = item.submissions?.length > 0;
            
            return (
              <Card key={item.id} className={`overflow-hidden ${isOverdue ? 'border-l-4 border-l-red-500' : ''} ${item.status === 'submitted' ? 'border-l-4 border-l-green-500' : ''}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className="font-semibold">{item.title}</h3>
                        <Badge className={`${colors.badge} text-white text-xs`}>
                          {BRAND_LABELS[item.brand]}
                        </Badge>
                        <Badge variant={item.priority === 'P0' ? 'destructive' : 'secondary'}>
                          {item.priority}
                        </Badge>
                        <Badge variant="outline" className={`capitalize ${
                          item.status === 'submitted' ? 'border-green-500 text-green-700' :
                          item.status === 'accepted' ? 'border-green-500 text-green-700 bg-green-50' :
                          ''
                        }`}>
                          {item.status.replace('-', ' ')}
                        </Badge>
                        {isOverdue && <Badge variant="destructive">Overdue</Badge>}
                        {hasSubmissions && (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            {item.submissions.length} submission{item.submissions.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{item.whyNeeded}</p>
                      <p className="text-sm">
                        <span className="font-medium">Deadline:</span> {item.deadline}
                        {item.formatRequired && (
                          <>
                            {' | '}
                            <span className="font-medium">Format:</span> {item.formatRequired}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {item.status === 'submitted' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            onClick={() => handleReview(item.id, 'accept')}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const reason = prompt('Reason for revision:');
                              if (reason) handleReview(item.id, 'needs-revision', reason);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Needs Revision
                          </Button>
                        </>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => onEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {hasSubmissions && (
                        <Button size="sm" variant="ghost" onClick={() => toggleExpand(item.id)}>
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Submissions Section */}
                {isExpanded && hasSubmissions && (
                  <div className="px-4 pb-4 border-t bg-slate-50">
                    <div className="mt-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">
                        Client Submissions ({item.submissions.length})
                      </p>
                      <div className="space-y-3">
                        {item.submissions.map((sub) => (
                          <div key={sub.id} className="p-3 bg-white rounded-lg border">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5">
                                {getSubmissionIcon(sub.type)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-slate-800">{sub.submittedBy?.name}</span>
                                  <span className="text-xs text-slate-500 capitalize">{sub.type.replace('-', ' ')}</span>
                                  <span className="text-xs text-slate-400">
                                    {new Date(sub.submittedAt).toLocaleString()}
                                  </span>
                                </div>
                                
                                {/* Text Content */}
                                {sub.textContent && (
                                  <div className="mt-2 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap border">
                                    {sub.textContent}
                                  </div>
                                )}
                                
                                {/* Note */}
                                {sub.note && sub.note !== sub.textContent && (
                                  <p className="mt-2 text-sm text-slate-600 italic bg-amber-50 p-2 rounded">
                                    Note: "{sub.note}"
                                  </p>
                                )}
                                
                                {/* File Download */}
                                {sub.type === 'file' && sub.fileUrl && (
                                  <a
                                    href={`${BACKEND_URL}${sub.fileUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-sm hover:bg-blue-100 transition-colors"
                                  >
                                    <Download className="h-4 w-4" />
                                    {sub.fileName || 'Download File'}
                                  </a>
                                )}
                                
                                {/* Link */}
                                {sub.type === 'link' && sub.linkUrl && (
                                  <a
                                    href={sub.linkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-md text-sm hover:bg-purple-100 transition-colors"
                                  >
                                    <LinkIcon className="h-4 w-4" />
                                    {sub.linkUrl.length > 60 ? sub.linkUrl.substring(0, 60) + '...' : sub.linkUrl}
                                  </a>
                                )}
                                
                                {/* Email indicator */}
                                {sub.type === 'shared-via-email' && (
                                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-md text-sm">
                                    <Mail className="h-4 w-4" />
                                    Client indicated they sent this via email
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Comments */}
                    {item.comments?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-slate-700 mb-2">Comments</p>
                        <div className="space-y-2">
                          {item.comments.map((comment) => (
                            <div key={comment.id} className="p-2 bg-white rounded border text-sm">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{comment.author?.name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {comment.authorType === 'admin' ? 'CV' : 'Client'}
                                </Badge>
                                <span className="text-xs text-slate-400">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-slate-600 mt-1">{comment.content}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
